import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import {
  DEFAULT_DOSSIER_FIELD_FLAGS,
  type DossierFieldFlags,
} from '../../lib/dossiers/dossierFields';
import {
  DEFAULT_DOSSIER_REGISTRY_SETTINGS,
  type DossierRegistrySettings,
} from '../../lib/dossiers/registrySettings';
import { DEFAULT_DATE_FORMAT, type DateFormatStyle } from '../../lib/dateFormat';
import { ALL_TASK_FIELD_FLAGS, type TaskFieldFlags } from '../../lib/tasks/taskFields';
import { ALL_TASK_VIEW_FLAGS, type TaskViewFlags } from '../../lib/tasks/taskViews';
import {
  type CabinetSettings,
  type DossierPermissionScope,
  getCabinetSettings,
  updateDateFormat,
  updateDossierFieldFlags,
  updateDossierPermissionScope,
  updateDossierRegistrySettings,
  updateTaskFieldFlags,
  updateTaskQuickCapture,
  updateTaskHoverPreview,
  updateTaskViewFlags,
} from '../../services/cabinet/cabinetSettingsService';
import { useAuthStore } from '../../store/useAuthStore';

function mergeCabinetSettings(
  prev: CabinetSettings | undefined,
  patch: Partial<CabinetSettings>,
): CabinetSettings {
  return {
    dossierPermissionScope: prev?.dossierPermissionScope ?? 'global',
    taskViews: prev?.taskViews ?? ALL_TASK_VIEW_FLAGS,
    taskFields: prev?.taskFields ?? ALL_TASK_FIELD_FLAGS,
    taskQuickCapture: prev?.taskQuickCapture ?? false,
    taskHoverPreview: prev?.taskHoverPreview ?? true,
    dossierFields: prev?.dossierFields ?? DEFAULT_DOSSIER_FIELD_FLAGS,
    dossierRegistry: prev?.dossierRegistry ?? DEFAULT_DOSSIER_REGISTRY_SETTINGS,
    dateFormat: prev?.dateFormat ?? DEFAULT_DATE_FORMAT,
    ...patch,
  };
}

export const cabinetSettingsKeys = {
  all: ['cabinet_settings', 'v2'] as const,
};

function useSessionReady() {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return isInitialized && !!session;
}

function useCabinetSettingsQuery<T = CabinetSettings>(
  select?: (row: CabinetSettings) => T,
) {
  return useQuery({
    ...STATIC,
    queryKey: cabinetSettingsKeys.all,
    queryFn: getCabinetSettings,
    enabled: useSessionReady(),
    select,
  });
}

export function useCabinetSettings() {
  return useCabinetSettingsQuery();
}

export function useDossierPermissionScope() {
  return useCabinetSettingsQuery((row) => row.dossierPermissionScope);
}

export function useTaskViewFlags() {
  return useCabinetSettingsQuery((row) => row.taskViews);
}

export function useTaskFieldFlags() {
  return useCabinetSettingsQuery((row) => row.taskFields);
}

export function useUpdateDossierPermissionScope() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (scope: DossierPermissionScope) =>
      updateDossierPermissionScope(scope, collaboratorId),
    onSuccess: (scope) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { dossierPermissionScope: scope }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

export function useUpdateTaskViewFlags() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (flags: TaskViewFlags) => updateTaskViewFlags(flags, collaboratorId),
    onSuccess: (taskViews) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { taskViews }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

export function useUpdateTaskFieldFlags() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (flags: TaskFieldFlags) => updateTaskFieldFlags(flags, collaboratorId),
    onSuccess: (taskFields) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { taskFields }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

export function useTaskQuickCapture() {
  return useCabinetSettingsQuery((row) => row.taskQuickCapture);
}

/** Aperçu au survol des tâches (réglage du cabinet). true tant que les réglages chargent. */
export function useTaskHoverPreview(): boolean {
  const { data } = useCabinetSettingsQuery((row) => row.taskHoverPreview);
  return data ?? true;
}

export function useUpdateTaskHoverPreview() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (enabled: boolean) => updateTaskHoverPreview(enabled, collaboratorId),
    onSuccess: (taskHoverPreview) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { taskHoverPreview }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

export function useUpdateTaskQuickCapture() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (enabled: boolean) => updateTaskQuickCapture(enabled, collaboratorId),
    onSuccess: (taskQuickCapture) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { taskQuickCapture }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

export function useDossierFieldFlags() {
  return useCabinetSettingsQuery((row) => row.dossierFields);
}

/** Prescription / aide juridictionnelle : visibles seulement si le réglage chargé est true. */
export function useEffectiveDossierFields(): DossierFieldFlags {
  const { data: flags } = useDossierFieldFlags();
  return {
    prescription: flags?.prescription === true,
    legalAid: flags?.legalAid === true,
  };
}

export function useUpdateDossierFieldFlags() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (flags: DossierFieldFlags) => updateDossierFieldFlags(flags, collaboratorId),
    onSuccess: (dossierFields) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { dossierFields }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

/** Réglages du registre. `undefined` tant que le cabinet n'est pas chargé. */
export function useDossierRegistrySettings() {
  return useCabinetSettingsQuery((row) => row.dossierRegistry);
}

export function useUpdateDossierRegistrySettings() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (settings: DossierRegistrySettings) =>
      updateDossierRegistrySettings(settings, collaboratorId),
    onSuccess: (dossierRegistry) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { dossierRegistry }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}

export function useCabinetDateFormat() {
  return useCabinetSettingsQuery((row) => row.dateFormat);
}

export function useUpdateDateFormat() {
  const qc = useQueryClient();
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);

  return useMutation({
    mutationFn: (style: DateFormatStyle) => updateDateFormat(style, collaboratorId),
    onSuccess: (dateFormat) => {
      qc.setQueryData<CabinetSettings>(cabinetSettingsKeys.all, (prev) =>
        mergeCabinetSettings(prev, { dateFormat }),
      );
      void qc.invalidateQueries({ queryKey: cabinetSettingsKeys.all });
    },
  });
}
